/*
  exporter.js — Data export module (version 1.3)

  Supports CSV and KML export.
  Nothing is sent to a server — all processing is local.
*/

/*
  exportCSV — exports filtered vessel records as a clean CSV file.
  Uses NOAA standard column names for compatibility.
*/
export function exportCSV(records, filename = "aissift_export.csv") {
  if (!records || records.length === 0) return;

  const headers = [
    "MMSI", "VesselName", "BaseDateTime",
    "LAT", "LON", "SOG", "COG", "Heading",
    "VesselType", "IMO", "CallSign",
  ];

  const rows = records.map((r) => [
    r.mmsi, r.vesselName, r.timestamp,
    r.lat, r.lng, r.speed, r.course, r.heading,
    r.vesselType, r.imo, r.callSign,
  ]);

  const csvContent = [
    headers.join(","),
    ...rows.map((row) =>
      row.map((cell) => {
        const str = String(cell ?? "");
        if (str.includes(",") || str.includes('"') || str.includes("\n")) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      }).join(",")
    ),
  ].join("\n");

  downloadFile(csvContent, filename, "text/csv");
}

/*
  exportKML — exports vessel records as a KML file for Google Earth.

  Features:
  - One LineString track per vessel
  - Individual timestamped Placemarks — enables Google Earth time slider
  - Vessel metadata (name, MMSI, speed, heading) in each point popup
  - Multi-vessel support — each vessel gets its own color-coded folder
  - Colors match the MapView track colors

  KML color format is AABBGGRR (alpha, blue, green, red).
*/
export function exportKML(records, filename) {
  if (!records || records.length === 0) return;
  if (!filename) filename = "aissift_export.kml";

  // Group records by MMSI
  const vesselMap = new Map();
  for (const r of records) {
    if (!vesselMap.has(r.mmsi)) vesselMap.set(r.mmsi, []);
    vesselMap.get(r.mmsi).push(r);
  }

  // KML AABBGGRR colors matching MapView.jsx VESSEL_COLORS
  const KML_COLORS = [
    "ffeb6325", // Blue   #2563eb
    "ff2626dc", // Red    #dc2626
    "ff3da016", // Green  #16a034
    "ff06a7d9", // Amber  #d97706
    "ffed3a7c", // Purple #7c3aed
  ];

  let colorIndex = 0;
  const styleDefs = [];
  const folderDefs = [];
  const nameTag = "name";
  const open = "<" + nameTag + ">";
  const close = "</" + nameTag + ">";

  vesselMap.forEach(function(vesselRecords, mmsi) {
    vesselRecords.sort(function(a, b) { return new Date(a.timestamp) - new Date(b.timestamp); });

    const vName = vesselRecords[0].vesselName || ("MMSI " + mmsi);
    const color = KML_COLORS[colorIndex % KML_COLORS.length];
    const styleId = "style_" + colorIndex;
    colorIndex++;

    // Style block
    styleDefs.push([
      '  <Style id="' + styleId + '">',
      "    <LineStyle><color>" + color + "</color><width>3</width></LineStyle>",
      "    <IconStyle><color>" + color + "</color><scale>0.6</scale></IconStyle>",
      "  </Style>",
    ].join("\n"));

    // Track LineString
    const coords = vesselRecords.map(function(r) { return r.lng + "," + r.lat + ",0"; }).join("\n          ");

    const trackBlock = [
      "    <Placemark>",
      "      " + open + escapeXml(vName) + " — Full Track" + close,
      '      <styleUrl>#' + styleId + "</styleUrl>",
      "      <LineString>",
      "        <tessellate>1</tessellate>",
      "        <coordinates>",
      "          " + coords,
      "        </coordinates>",
      "      </LineString>",
      "    </Placemark>",
    ].join("\n");

    // Individual timestamped position Placemarks
    const pointBlocks = vesselRecords.map(function(r) {
      const when = formatKMLTimestamp(r.timestamp);
      return [
        "    <Placemark>",
        "      " + open + escapeXml(vName) + close,
        '      <styleUrl>#' + styleId + "</styleUrl>",
        "      <TimeStamp><when>" + when + "</when></TimeStamp>",
        "      <description><![CDATA[" +
          "<b>" + escapeXml(vName) + "</b><br/>" +
          "MMSI: " + r.mmsi + "<br/>" +
          "Time: " + r.timestamp + "<br/>" +
          "Speed: " + r.speed + " knots<br/>" +
          "Heading: " + r.heading + "°<br/>" +
          "Course: " + r.course + "°" +
        "]]></description>",
        "      <Point><coordinates>" + r.lng + "," + r.lat + ",0</coordinates></Point>",
        "    </Placemark>",
      ].join("\n");
    }).join("\n");

    folderDefs.push([
      "  <Folder>",
      "    " + open + escapeXml(vName) + " (MMSI: " + mmsi + ")" + close,
      "    <open>1</open>",
      trackBlock,
      pointBlocks,
      "  </Folder>",
    ].join("\n"));
  });

  const kml = [
    '<?xml version="1.0" encoding="UTF-8"?>',
    '<kml xmlns="http://www.opengis.net/kml/2.2">',
    "<Document>",
    "  " + open + "AISsift Export" + close,
    "  <description>Vessel track exported from AISsift.</description>",
    styleDefs.join("\n"),
    folderDefs.join("\n"),
    "</Document>",
    "</kml>",
  ].join("\n");

  downloadFile(kml, filename, "application/vnd.google-earth.kml+xml");
}


/*
  formatKMLTimestamp — converts AIS timestamp to KML ISO 8601 format.
  NOAA format: 2024-01-01T06:00:00 → needs Z suffix for UTC.
*/
function formatKMLTimestamp(timestamp) {
  if (!timestamp) return "";
  if (timestamp.endsWith("Z") || timestamp.includes("+")) return timestamp;
  return timestamp.replace(" ", "T") + "Z";
}

/*
  escapeXml — escapes special characters for safe XML embedding.
*/
function escapeXml(str) {
  if (!str) return "";
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

/*
  downloadFile — triggers a browser file download.
*/
function downloadFile(content, filename, mimeType) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
